import { useSessionStorage } from "@uidotdev/usehooks";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import APIS from "../../constant/EndPoint";
import { IFilterPickup } from "../../types/interface";
import { hasValues } from "../../utils/hasValues";
import Card from "../Reusable/Card";
import DataTable from "../Reusable/DataTable";
import NACell from "../Reusable/DataTable/NACell";

import { tw } from "@careinfo/tailwind";
import PickupRequestFilter from "../InternationalPickupRequest/PickupRequestFilter";
import {
  AccessControl,
  ClientAccessControl,
} from "@careinfo/permission-required";
import { classNames, responsiveClassName } from "../../utils/classNames";
import DestinationCell from "../Reusable/OrderDataTable/DestinationCell";
import TabPanel from "../Reusable/Tab";
import ScanQrCode from "../ScanQrCode";
import PickupOrderList from "./PickupOrderList";
import { OrderList } from "../../Pages/CourierPickupRequest/PickupOrderList";
import TrackingCell from "../Reusable/DataTable/TrackingCell";
import { getKey } from "../../helpers/sessionKey";
import OperationCombobox from "../Reusable/OpeationCombobox";
import Modal from "../Reusable/Modal";
import Suborders from "./Suborders";
import OrderMenu from "./ConsignmentMenu";

const operationData = [
  { id: "Pickup (Receive)", name: "Pickup (Receive)" },
  { id: "Manifest (Receive)", name: "Manifest (Receive)" },
  { id: "Delivery", name: "Delivery" },
];
const CourierPickupRequestList = () => {
  const navigate = useNavigate();

  const [localState, handleSetState] = useSessionStorage<IFilterPickup>(
    "pickup-request",
    {
      created__range: null,
      vehicle: null,
      driver: null,
      merchant: null,
      operations: null,
    }
  );
  const [openDeliveryformPopup, setOpenDeliveryformPopup] = useState(false);
  const [isFilterVisible, setFilterVisiable] = useState(hasValues(localState));
  const [openSuborderpopup, setOpensuborderPopup] = useState(false);
  const [orderActionMenu, setOrderActionMenu] = useState(false);
  const [actionType, setActionType] = useState("");
  const [searchParams, setSearchParms] = useSearchParams();

  const [rowid, setRowid] = useState();

  const {
    setValue,
    getValues,
    watch,
    reset,
    clearErrors,
    setError,
    register,
    control,
    formState: { errors },
  } = useForm<IFilterPickup>({
    defaultValues: async () => ({
      created__range: localState?.created__range ?? null,
      vehicle: localState?.vehicle ?? null,
      driver: localState?.driver ?? null,
      merchant: localState?.merchant ?? null,
      operations: localState?.operations ?? null,
    }),
  });

  const handleFilterReset = () => {
    reset({
      created__range: null,
      vehicle: null,
      driver: null,
      merchant: null,
      operations: null,
    });
  };
  const [isactive, setISactive] = useState("trackbutton");

  const handleactiveButton = (button) => {
    setISactive(button);
  };
  const checkedAllItems = searchParams.get("selectAllItems") === "1";
  const selectedOrderDetails = searchParams.get("selectedItem");
  const location = useLocation();

  const appendSelectedItem = useCallback(
    (selectedItem: any) => {
      const params = new URLSearchParams(location.search);
      params.set("selectedItem", selectedItem);

      const newFullPath = `${location.pathname}?${params.toString()}`;

      navigate(newFullPath, { replace: true });
    },
    [location.pathname, location.search, navigate]
  );

  const handleIndividualSelect = useCallback(
    (value: string) => {
      let selectedItems = searchParams.get("selectedItems")?.split(",");
      if (!selectedItems || selectedItems === undefined) {
        searchParams.set("selectedItems", value);
        setSearchParms(searchParams);
        return;
      }
      let itemIndex = selectedItems?.findIndex((item) => item === value);
      if (itemIndex > -1) {
        selectedItems.splice(itemIndex, 1);
        const newSelectedItems = selectedItems
          ?.map((item) => item)
          .join(",")
          .toString();
        if (newSelectedItems.length) {
          searchParams.set("selectedItems", newSelectedItems);
        } else {
          searchParams.delete("selectedItems");
        }
        searchParams.set("selectAllItems", "0");
        setSearchParms(searchParams);
      } else {
        selectedItems?.push(value);
        const newSelectedItems = selectedItems
          ?.map((item) => item)
          .join(",")
          .toString();
        searchParams.set("selectedItems", newSelectedItems);
        setSearchParms(searchParams);
      }
    },
    [searchParams, setSearchParms]
  );

  const isSelected = useCallback(
    (value: string) => {
      const selectedItems = searchParams.get("selectedItems")?.split(",");
      if (!selectedItems) {
        return false;
      }
      return selectedItems.includes(value);
    },
    [searchParams]
  );

  const handleSelectAll = useCallback(
    (table: any) => {
      const {
        options: { data: tableData },
      } = table;
      const isSelected = searchParams.get("selectAllItems");
      if (isSelected === "1") {
        searchParams.set("selectAllItems", "0");
        searchParams.delete("selectedItems");
        setSearchParms(searchParams);
      } else {
        const selectedItems = tableData
          ?.map((item: any) => item.id)
          .join(",")
          .toString();
        searchParams.set("selectAllItems", "1");
        searchParams.set("selectedItems", selectedItems);
        setSearchParms(searchParams);
      }
    },
    [searchParams, setSearchParms]
  );

  const defaultColumns: any = [
    {
      header: "Pickup Manifest ID",
      accessorKey: "tracking_number",
      cell: NACell,
    },
    {
      header: "Created",
      accessorKey: "created",
      cell: ({ getValue }: any) => {
        return (
          <>
            <p>
              {getValue()
                ? moment(getValue()).format("YY-MM-DD HH:mm:ss")
                : "N/A"}
            </p>
          </>
        );
      },
    },
    {
      header: "Sender",
      accessorKey: "sender_name",
      cell: ({ row, getValue }: any) => (
        <div>
          <p>{getValue()}</p>
          <p className={tw`text-xs`}>{row?.original?.sender_primary_contact}</p>
          <p className={tw`text-xs`}>{row?.original?.sender_address}</p>
        </div>
      ),
    },
    {
      header: "Driver",
      accessorKey: "driver.user.full_name",
      cell: NACell,
    },

    {
      header: "Total Items",
      accessorKey: "order_count",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>{getValue() ?? 0}</p>
          </>
        );
      },
    },
    {
      header: "Received Items",
      accessorKey: "items.received",
      cell: ({ getValue }: any) => {
        return (
          <>
            <p>{getValue() ?? 0.0}</p>
          </>
        );
      },
    },
    {
      header: "Missing Items",
      accessorKey: "items.missing",
      cell: ({ getValue }: any) => {
        return (
          <>
            <p>{getValue() ?? 0.0}</p>
          </>
        );
      },
    },
    // {
    //   header: "Received",
    //   accessorKey: "is_received",
    //   cell: ({ getValue }: any) => {
    //     return (
    //       <>
    //         {getValue() ? (
    //           <svg
    //             width="24"
    //             height="24"
    //             viewBox="0 0 24 24"
    //             fill="none"
    //             xmlns="http://www.w3.org/2000/svg"
    //           >
    //             <path
    //               d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86M22 4L12 14.01L9.00001 11.01"
    //               stroke="#26C943"
    //               stroke-width="2"
    //               stroke-linecap="round"
    //               stroke-linejoin="round"
    //             />
    //           </svg>
    //         ) : (
    //           <svg
    //             width="24"
    //             height="24"
    //             viewBox="0 0 24 24"
    //             fill="none"
    //             xmlns="http://www.w3.org/2000/svg"
    //           >
    //             <path
    //               d="M15 9L9 15M9 9L15 15M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
    //               stroke="red"
    //               stroke-width="2"
    //               stroke-linecap="round"
    //               stroke-linejoin="round"
    //             />
    //           </svg>
    //         )}
    //       </>
    //     );
    //   },
    // },
  ];

  let Columns: any = [
    {
      header: "WayBill",
      accessorKey: "tracking_number",
      cell: ({ row, getValue }: any) => {
        return (
          <button
            className={tw`cursor-pointer focus:outline-none px-4 w-fit relative`}
          >
            {row?.original?.is_on_hold && (
              <span
                className={tw` absolute -top-2 text-[10px] -right-5 text-white px-[4px] bg-red-400 rounded-full `}
              >
                H
              </span>
            )}
            <ClientAccessControl allowedPermissions={["can_view_order_id"]}>
              <p
                className={tw`text-xs text-gray-500 text-center whitespace-nowrap`}
              >
                {row?.original?.order_number}
              </p>
            </ClientAccessControl>
            <ClientAccessControl
              children={
                <p
                  className={classNames(
                    row?.original?.is_duplicate
                      ? tw`text-red-600 text-bold `
                      : "",
                    tw`whitespace-nowrap text-center`
                  )}
                >
                  {getValue() ?? "N/A"}
                </p>
              }
              allowedPermissions={["can_view_tracking"]}
            />
            <p
              className={tw`text-xs text-gray-500 text-center whitespace-nowrap`}
            >
              {moment(row?.original?.created).format("YY-MM-DD HH:mm:ss")}
            </p>
            <ClientAccessControl
              children={
                <p className={tw`text-xs text-gray-500 uppercase text-center`}>
                  {row?.original?.service_type?.name}
                </p>
              }
              allowedPermissions={["can_view_service_type"]}
            />
            {row?.original?.is_pickup_created && (
              <span
                className={tw` absolute -top-2 text-[10px] -right-1 text-white px-[4px] bg-red-400 rounded-full `}
              >
                P
              </span>
            )}
          </button>
        );
      },
    },
    {
      header: "Date",
      accessorKey: "created",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className={tw`w-[100px]`}>
              {getValue()
                ? moment(getValue()).format("YY-MM-DD HH:mm:ss")
                : "N/A"}
            </p>
          </>
        );
      },
    },
    {
      header: "Origin",
      accessorKey: "pickup_branch.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className={tw`uppercase`}>{getValue()}</p>
            <p className={tw`text-xs text-primary`}>
              {row?.original?.pickup_branch?.warehouse?.name}
            </p>
          </>
        );
      },
    },
    {
      header: "Sender",
      accessorKey: "sender.full_name",
      cell: ({ row }: any) => {
        return (
          <>
            <ClientAccessControl
              children={
                <p className={tw``}>
                  {row?.original?.sender?.full_name ?? "N/A"}{" "}
                </p>
              }
              allowedPermissions={["can_view_sender_name"]}
            />
            <ClientAccessControl
              children={
                <p className={tw`whitespace-nowrap text-sm`}>
                  {row?.original?.sender?.primary_contact ?? "N/A"}{" "}
                </p>
              }
              allowedPermissions={["can_view_sender_number"]}
            />
          </>
        );
      },
    },
    {
      header: "Receiver",
      accessorKey: "receiver.full_name",
      cell: ({ row }: any) => {
        return (
          <div className={tw`w-full`}>
            <ClientAccessControl
              children={
                <p className={tw``}>
                  {row?.original?.receiver.full_name ?? "N/A"}
                </p>
              }
              allowedPermissions={["can_view_receiver_name"]}
            />
            <ClientAccessControl
              children={
                <p className={tw`whitespace-nowrap text-sm`}>
                  {row?.original?.receiver.primary_contact ?? "N/A"}{" "}
                </p>
              }
              allowedPermissions={["can_view_receiver_number"]}
            />
            <ClientAccessControl
              children={
                <p className={tw`text-sm w-full max-w-[200px]`}>
                  {row?.original?.receiver.address ?? "N/A"}{" "}
                </p>
              }
              allowedPermissions={["can_view_receiver_location"]}
            />
          </div>
        );
      },
    },
    {
      header: "Destination",
      accessorKey: "destination.name",
      cell: DestinationCell,
    },
    {
      header: "Pieces",
      accessorKey: "piece",
      cell: ({ getValue }: any) => {
        return (
          <>
            <ClientAccessControl
              children={
                <p>
                  {getValue()}
                  <span className="text-gray-500 text-xs">pieces</span>{" "}
                </p>
              }
              allowedPermissions={["can_view_piece"]}
            />
          </>
        );
      },
    },
    {
      header: "Remarks",
      accessorKey: "remark",
      cell: ({ row, getValue }: any) => (
        <>
          <ClientAccessControl
            children={
              <button className={tw`focus:outline-none`}>
                {getValue()} Remarks
              </button>
            }
            allowedPermissions={["can_view_remarks"]}
            renderNoAccess={<p>{getValue()} Remarks</p>}
          />
        </>
      ),
    },
    {
      header: "Status",
      accessorKey: "delivery_status.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <ClientAccessControl
              children={
                <>
                  <p
                    className={tw`uppercase px-1 text-center rounded-md`}
                    style={{
                      background: row?.original?.delivery_status?.color,
                    }}
                  >
                    {getValue()}
                  </p>
                  <p className={tw`text-xs text-primary `}>
                    {row?.original?.rider?.name}
                  </p>
                </>
              }
              allowedPermissions={["can_view_status"]}
            />
          </>
        );
      },
    },
  ];
  let DraftColumns: any = [
    {
      id: "select",
      header: ({ table }: any) => {
        return (
          <input
            type="checkbox"
            checked={checkedAllItems}
            onChange={() => handleSelectAll(table)}
            className={tw`cursor-pointer`}
          />
        );
      },
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={isSelected(row?.original?.id)}
          onChange={() => handleIndividualSelect(row?.original?.id)}
          className={tw`cursor-pointer`}
        />
      ),
    },
    {
      header: "WayBill",
      accessorKey: "tracking_number",
      cell: TrackingCell,
    },
    {
      header: "Ref No.",
      accessorKey: "reference_number",
      cell: NACell,
    },
    {
      header: "Merchant",
      accessorKey: "merchant.name",
      cell: NACell,
    },
    {
      header: "Origin",
      accessorKey: "pickup_branch.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className={tw`uppercase`}>{getValue()}</p>
            <p className={tw`text-xs text-primary`}>
              {row?.original?.destination_branch?.warehouse?.name}
            </p>
          </>
        );
      },
    },
    {
      header: "Destination",
      accessorKey: "destination.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className={tw`uppercase`}>{getValue()}</p>
            <p className={tw`text-xs text-primary`}>
              {row?.original?.destination_branch?.warehouse?.name}
            </p>
          </>
        );
      },
    },
    {
      header: "Receiver",
      accessorKey: "receiver.full_name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className={tw`uppercase`}>{getValue()}</p>
            <p className={tw`text-xs `}>
              {row?.original?.receiver?.primary_contact}
            </p>
            <p className={tw`text-xs  `}>{row?.original?.receiver?.address}</p>
          </>
        );
      },
    },
    {
      header: "Finance",
      accessorKey: "delivery_charge",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className="whitespace-nowrap">
              COD: {row?.original?.cod ?? 0.0}
            </p>
            <p className="whitespace-nowrap">
              Collected: {row?.original?.collected_cod ?? 0.0}
            </p>
            <p className="whitespace-nowrap">D. Charge:{getValue()}</p>
          </>
        );
      },
    },
    {
      header: "Weight",
      accessorKey: "weight",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>
              {getValue()}
              <span className="text-gray-500 text-xs">
                {row?.original?.package_type?.unit}
              </span>{" "}
            </p>
          </>
        );
      },
    },
    {
      header: "Piece",
      accessorKey: "piece",
      cell: ({ getValue, row }: any) => {
        return (
          <>
            <ClientAccessControl allowedPermissions={["can_view_piece"]}>
              <button
                className={tw`cursor-pointerb focus:outline-none`}
                onClick={() => {
                  setOpensuborderPopup(!openSuborderpopup);
                  setRowid(row?.original?.id);
                }}
              >
                <p>{getValue()}</p>
              </button>
            </ClientAccessControl>
          </>
        );
      },
    },
    {
      header: "Remarks",
      accessorKey: "remark",
      cell: ({ row, getValue }: any) => <p>{getValue()} Remarks</p>,
    },
    {
      header: "Status",
      accessorKey: "delivery_status.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p
              className={tw`uppercase px-1 text-center rounded-md`}
              style={{ background: "#1C90E9" }}
            >
              Draft
            </p>
          </>
        );
      },
    },
  ];
  const handleEditNavigate = (row: any) => {
    navigate(`edit/${row?.original?.id}`);
  };

  const filterParams = watch();

  useEffect(() => {
    handleSetState({
      ...localState,
      ...filterParams,
    });
  }, [filterParams]);

  const operations = filterParams["operations"];

  const params = useCallback(() => {
    const newValue = {};
    for (const [key, value] of Object.entries(filterParams)) {
      if (value) {
        if (Array.isArray(value)) {
          newValue[key] = value.map((v) => v.value).join(",");
        } else if (typeof value === "object") {
          newValue[key] = value.id;
        } else if (!value) {
          newValue[key] = null;
        } else {
          newValue[key] = value;
        }
      }
    }
    return newValue;
  }, [filterParams]);

  const clientType = getKey("client-type");
  return (
    <>
      <div>
        <Card
          title="Filters"
          onClick={() => setFilterVisiable(!isFilterVisible)}
        >
          <PickupRequestFilter
            isFilterVisible={isFilterVisible}
            setValue={setValue}
            getValues={getValues}
            handleFilterReset={handleFilterReset}
            control={control}
          />
        </Card>
      </div>

      <TabPanel
        tabTitle={
          // clientType === "logistic"
          //   ? ["Draft Order"]
          //   :
          ["Pickup Request", "Pickup Order", "Draft Order"]
        }
        tabPanels={
          // clientType === "logistic"
          //   ? [
          //       <div className={tw`flex flex-col mb-4`}>
          //         <DataTable
          //           url={`${APIS.ORDER}`}
          //           queryKey={"order"}
          //           columns={DraftColumns}
          //           orderSearch={false}
          //           subOrderSearch={true}
          //           tableName={"draft-table"}
          //           params={{ ...params(), is_draft: true }}
          //           actionMenu={
          //             <div
          //               className={tw`flex w-full whitespace-nowrap items-center`}
          //             >
          //               <div className={tw`w-1 relative -top-2 text-black`}>
          //                 <OperationCombobox
          //                   data={operationData}
          //                   register={register("operations")}
          //                   errors={errors?.operations}
          //                   name={"operations"}
          //                   setValue={setValue}
          //                   placeholder={"Select"}
          //                   getValues={getValues}
          //                   clearErrors={clearErrors}
          //                   hidden={true}
          //                 />
          //               </div>
          //               <AccessControl
          //                 children={
          //                   <ScanQrCode
          //                     operations={operations}
          //                     isactive={isactive}
          //                     setValue={setValue}
          //                     setError={setError}
          //                     clearErrors={clearErrors}
          //                     setOpenDeliveryformPopup={
          //                       setOpenDeliveryformPopup
          //                     }
          //                   />
          //                 }
          //                 allowedPermissions={["can_scan_barcode"]}
          //               />
          //               <AccessControl
          //                 allowedPermissions={["can_perform_action"]}
          //               >
          //                 <OrderMenu
          //                   orderActionMenu={orderActionMenu}
          //                   setOrderActionMenu={setOrderActionMenu}
          //                   setActionType={setActionType}
          //                 />
          //               </AccessControl>
          //             </div>
          //           }
          //         />
          //       </div>,
          //     ]
          //   :
          [
            <div>
              <DataTable
                url={`${APIS.PICKUPREQUEST}`}
                queryKey={"pickup-request"}
                handleEditNavigate={handleEditNavigate}
                columns={defaultColumns}
                orderSearch={true}
                tableName={"pickup-request"}
                addNewPagePath={"create"}
                params={{ ...params(), ordering: "is_received,-created" }}
              />
            </div>,
            <div className={tw`flex flex-col mb-4`}>
              <OrderList />
            </div>,
            <div className={tw`flex flex-col mb-4`}>
              <DataTable
                url={`${APIS.ORDER}`}
                queryKey={"order"}
                columns={DraftColumns}
                orderSearch={true}
                tableName={"draft-table"}
                params={{ ...params(), is_draft: true }}
              />
            </div>,
          ]
        }
      />
      <Modal
        isOpen={openSuborderpopup}
        setIsOpen={setOpensuborderPopup}
        title={"Sub Orders"}
      >
        <div className={tw`w-[600px] `}>
          <Suborders id={rowid} />
        </div>
      </Modal>
    </>
  );
};

export default CourierPickupRequestList;

